/**
 * This should handle all the messages and data loading for the device status.
 */

import { DeviceDao } from '@/dao';
import { DeviceStatus, StationDeviceStatus, StringArray } from '@/objects/generated';
import signalrSubscribe from '@/objects/generated/signalr/signalrSubscribe';
import { useEventCommonStore, useEventStoreRef, useUserStoreRef } from '@/stores';
import { ref, watch } from 'vue';
import { useGlobalMessages } from '../messages/globalMessages';
import { $ti } from '@/locize';
import { defineStore } from 'pinia';
import { useRouter, type RouteLocationNormalizedLoaded } from 'vue-router';

export const useDeviceStatus = defineStore('devicestatus', () => {
    let currentEventID: number = 0;
    /**
     * A reactive list of all the device statuses for the current event.
     */
    const deviceStatuses = ref<StationDeviceStatus[]>([]);

    const allreadyProcessedMessages: Map<string, Date> = new Map();

    /**
     * A reactive list of all the device ids that have been updated.
     * This is used to update the device statuses when we get a signalR message for a device status.
     * This will help us to update the grid for only the affected rows
     */
    const updatedDeviceIds = ref<string[]>([]);

    /**
     * A reactive map of all the device statuses for all events.
     * This is only available for super admins.
     */
    const globalDeviceStatuses = ref<Map<number, StationDeviceStatus[]>>(new Map());

    /**
     * A property to check if the current event is an active/running event
     */
    const isActiveEvent = ref<boolean | null>(null);

    /**
     * A property to check if we should listen for global statuses.
     * If this is set, we will subscribe for SignalR messages for global device statuses.
     * And we will also enable the globalMessages for the device statuses.
     */
    const listenForGlobalStatuses = ref<boolean>(false);

    /**
     * This property is set when we are currently viewing the super admin device status page.
     * We use this one to check if we should listen for signalR statuses or not.
     */
    const onSuperAdminDeviceStatusPage = ref<boolean>(false);

    /**
     * The id/handle on the interval so we can clear it when needed.
     */
    let updateStatusIntervalId: number | null = null;

    // A reference to the user so we can check if the user is a super admin.
    const { user } = useUserStoreRef();

    // A reference to the eventID so we can set the eventID for the device status.
    const { eventID } = useEventStoreRef();

    // A reference to the global messages so we can add messages for the device statuses
    const { AddMessage } = useGlobalMessages();

    // We need the router to get the current route. and lsiten for route changes.
    const route = useRouter();

    /**
     * We set the eventID for the device status.
     * @param id
     */
    const setEventID = async (id: number) => {
        if (id == currentEventID) return;

        const comStore = useEventCommonStore();
        await comStore.EnsureLoaded(['Event']);

        const event = comStore.Event;

        if (event == null) {
            console.warn('Event is not set, cannot set eventID for DeviceStatus');
            return;
        }

        isActiveEvent.value = event.EventStart <= new Date() && event.EventEnd >= new Date();

        currentEventID = event.ID;

        if (updateStatusIntervalId != null) {
            window.clearInterval(updateStatusIntervalId);
            updateStatusIntervalId = null;
        }

        loadStationDevicesStatus();

        if (isActiveEvent.value) {
            console.log('Starting device status update interval for event', eventID);
            startUpdateIntervalForEvent();
        }
    };

    /**
     * Start the interval to update the device statuses
     * for the current event.
     */
    const startUpdateIntervalForEvent = () => {
        updateStatusIntervalId = window.setInterval(async () => {
            const deviceIds = StringArray.FromJsValues({
                Values:
                    deviceStatuses.value
                        .filter((x) => (x.DeviceStatus?.DeviceId ?? '').length > 0)
                        .map((x) => x.DeviceStatus?.DeviceId ?? '') ?? [],
            });

            if (deviceIds.Values.length == 0) return;

            const statuses = await DeviceDao.GetDeviceStatusForDevices(eventID.value, deviceIds);

            // Merge the statuses into the existing statuses.
            statuses.forEach((status) => {
                const item = deviceStatuses.value.find((x) => x.DeviceStatus?.DeviceId == status.DeviceId);
                if (item != null) {
                    item.DeviceStatus = status;
                }
            });
        }, 10000);
    };

    /**
     * Load the device statuses for the current event.
     */
    const loadStationDevicesStatus = async () => {
        if (eventID.value == 0) return;

        deviceStatuses.value = await DeviceDao.GetStationDeviceStatusForEvent(eventID.value);

        // Check if we have conflicts
        deviceStatuses.value.forEach((status) => {
            addConflictMessage(status, eventID.value);
            addGeneralAlertMessage(status, eventID.value);
        });
    };

    const addConflictMessage = (status: StationDeviceStatus, eventId: number = 0) => {
        if (status.Conflicts?.length > 0) {
            status.Conflicts.forEach((conflict) => {
                const params = {
                    deviceid: status.DeviceStatus?.DeviceId,
                    eventname: status?.Station?.EventName ?? 'na',
                    conflictedevent: conflict.EventName,
                };
                AddMessage(
                    {
                        message: $ti(eventId == 0 ? 'devicestatus.conflictedeventglobal' : 'devicestatus.conflictedevent', params, true),
                        type: 'warning',
                        src: 'DeviceStatus',
                        dismisskey:
                            eventId == 0 ? 'devicestatus.conflictedeventglobal' : 'devicestatus.conflictedevent' + JSON.stringify(params),
                    },
                    eventId,
                );
            });
        }
    };

    /**
     * Add a general alert message for the device status.
     * @param status
     * @param eventId
     * @returns
     */
    const addGeneralAlertMessage = (status: StationDeviceStatus | null, eventId: number = 0) => {
        if (status == null || !status.DeviceStatus?.HasMessageAlert) return;
        const params = {
            deviceid: status.DeviceStatus?.DeviceId,
            eventname: (status?.Station?.EventName ?? '' == '') ? 'na' : status?.Station?.EventName,
            message: status.DeviceStatus.Messages?.find((x) => x.TypeText == 'GeneralAlert')?.Value,
            time: status.DeviceStatus.Messages?.find((x) => x.TypeText == 'GeneralAlert')?.Time,
        };
        AddMessage(
            {
                message: $ti('devicestatus.generalalert', params, true),
                type: 'danger',
                src: 'DeviceStatus',
                dismisskey: 'devicestatus.generalalert' + JSON.stringify(params),
            },
            eventId,
        );
    };

    const addMessageAlert = (status: DeviceStatus, eventId: number = 0) => {
        const msg = allreadyProcessedMessages.get(status.DeviceId ?? '');

        if (!status.DeviceId || (msg && msg > new Date())) {
            return;
        }

        allreadyProcessedMessages.set(status.DeviceId, new Date(new Date().getTime() + 180000));

        const params = {
            deviceid: status.DeviceId,
            eventname: '',
            message: status.Messages?.find((x) => x.TypeText == 'GeneralAlert')?.Value,
            time: status.Messages?.find((x) => x.TypeText == 'GeneralAlert')?.Time,
        };
        AddMessage(
            {
                message: $ti('devicestatus.generalalert', params, true),
                type: 'danger',
                src: 'DeviceStatus',
                dismisskey: 'devicestatus.generalalert' + JSON.stringify(params),
            },
            eventId,
        );
    };

    /**
     * This will load the global device statuses for all events and group them by eventID.
     */
    const loadGlobalDeviceStatuses = async () => {
        const statuses = await DeviceDao.GetStationDeviceStatuses(StringArray.FromJsValues({}));

        // We need to group the statuses by eventID and add them to the map
        const map = new Map<number, StationDeviceStatus[]>();
        statuses.forEach((status: StationDeviceStatus) => {
            const eventId = status.Station?.EventID ?? 0;
            if (!map.has(eventId)) {
                map.set(eventId, []);
            }

            map.get(eventId)?.push(status);

            addConflictMessage(status, 0);
            addGeneralAlertMessage(status, 0);
        });
        globalDeviceStatuses.value = map;
    };

    /**
     * Add a missing device status to the global device statuses.
     * @param status
     */
    const addDeviceStatusToGlobal = async (status: DeviceStatus) => {
        const stationDeviceStatuses = await DeviceDao.GetStationDeviceStatuses(StringArray.FromJsValues({ Values: [status.DeviceId!] }));

        if (stationDeviceStatuses.length == 0) return;

        const sds = stationDeviceStatuses[0];

        addConflictMessage(sds, 0);
        addGeneralAlertMessage(sds, 0);

        const mapitem = globalDeviceStatuses.value.get(sds.Station?.EventID ?? 0);
        if (mapitem == null) {
            globalDeviceStatuses.value.set(sds.Station?.EventID ?? 0, [sds]);
        } else {
            mapitem.push(sds);
        }
    };

    /**
     * Handle the global device statuses from the signalr subscription.
     * @param status
     */
    const handleGlobalDeviceStatus = (status: DeviceStatus[]) => {
        // We stop listening for global statuses if we are not on the global status page and we have not checked the setting for listening for global statuses.
        if (!listenForGlobalStatuses.value && !onSuperAdminDeviceStatusPage.value) return;

        const _updatedDeviceIds: string[] = [];

        status.forEach((s: DeviceStatus) => {
            if (!s) {
                return;
            }

            // Check if the device is allready in the dataset
            let existingItem: StationDeviceStatus | null = null;

            for (const value of globalDeviceStatuses.value.values()) {
                existingItem = value.find((x: StationDeviceStatus) => x.DeviceStatus?.DeviceId == s.DeviceId) ?? null;
                if (existingItem != null) {
                    break;
                }
            }

            if (existingItem != null) {
                existingItem.DeviceStatus = s;
            } else {
                addDeviceStatusToGlobal(s);
            }

            if (s.DeviceId != null && applyWarningsAndErrors(s)) {
                _updatedDeviceIds.push(s.DeviceId);
            }
        });
        updatedDeviceIds.value = _updatedDeviceIds;
    };

    /**
     * We apply all the warnings and errors to the device status. if nothing is applied we return false so we can skip the UI update.
     * @param status
     * @returns
     */
    const applyWarningsAndErrors = (status: DeviceStatus): boolean => {
        if (status.HasMessageAlert) {
            addMessageAlert(status, 0);
            return true;
        }
        return false;
    };

    /**
     * We listen for routechanges to see if we should listen for SignalR messages for global device statuses.
     * @param routechange
     */
    const checkRoute = (routechange: RouteLocationNormalizedLoaded) => {
        onSuperAdminDeviceStatusPage.value = routechange.name == 'devicestatus';
    };

    /**
     * Internal watch for the user store. We use this one to check if the user is loaded and if the user is a super admin.
     * Then we should start loading the global device statuses.
     */
    watch(
        user,
        async (newVal, oldVal) => {
            if (!oldVal && newVal && newVal.IsSuperAdmin) {
                console.log('User is super admin, loading global device statuses');
                loadGlobalDeviceStatuses();

                // Subscribe to the global device statuses
                signalrSubscribe.DeviceStatus(handleGlobalDeviceStatus);
            }
        },
        { immediate: true },
    );

    // We start a watch on the eventID to set the eventID for the device status.
    watch(eventID, (newVal) => setEventID(newVal), { immediate: true });

    // We watch the route to see if we are currently on the global device status page.
    watch(route.currentRoute, (routechange) => checkRoute(routechange), { immediate: true });

    return {
        /**
         * True if the event is currently active
         */
        IsActiveEvent: isActiveEvent,
        /**
         * Enable the global device statuses. This mean that we get signalR messages for all the device statuses and
         * we get warnings and error messages in the header.
         */
        ListenForGlobalStatuses: listenForGlobalStatuses,

        /**
         * A reactive list of all the device statuses for the current event.
         */
        EventDeviceStatuses: deviceStatuses,

        /**
         * A property to check if the current event is an active/running event
         */
        EventDeviceStatusActive: isActiveEvent,

        /**
         * A reactive map of all the device statuses for all events.
         * This is only available for super admins.
         */
        GlobalDeviceStatuses: globalDeviceStatuses,

        /**
         * A list of all the latest updated device ids.
         * Since we batch the updates in the backend we can listen to this to know what devices have been updated.and what rows to update.
         */
        UpdatedDeviceIds: updatedDeviceIds,
    };
});
